import React, { useEffect } from 'react';
import { Error as ErrorIcon } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { EXTENSION_ERROR_MESSAGE } from 'common/constants/documents';
import { Alert } from 'components';
import useDocumentsContext from 'context/DocumentsContext';
import { useDocuments } from 'services/hooks';
import { UploadedFileAlertProps } from './FileAlerts.props';
import ProgressBar from '../progress/ProgressBar';

export enum SecondaryFileClassification {
  CAP_TABLE = 'CAP_TABLE',
  BALANCE_SHEET = 'BALANCE_SHEET',
  INCOME_STATEMENT = 'INCOME_STATEMENT',
  CASH_FLOW_STATEMENT = 'CASH_FLOW_STATEMENT',
  OTHER = 'OTHER',
}

interface FileCreateClassification {
  secondary_classification: SecondaryFileClassification;
  confidence: number;
  auto_processing_skipped: boolean;
}

interface FileCreateResponse {
  generation_job_id?: string;
  classification?: FileCreateClassification;
}

const UploadedFileAlert = ({
  droppedFiles,
  file,
  validFileTypes,
  fromReferenceWidget,
  selectedMeasurementDateId,
  setIsFileUploaded,
  setFilesToSave,
  autoClassify = false,
}: UploadedFileAlertProps) => {
  const { validateAndUploadFile, progress, isValidType, setIsLoading } = useDocuments();
  const { currentDocuments, setCompanyDocuments } = useDocumentsContext();

  useEffect(() => {
    const execUpload = async () => {
      const fileProps = {
        file,
        fromReferenceWidget,
        mdId: selectedMeasurementDateId,
        currentDocumentsId: currentDocuments.id,
        files: currentDocuments.files,
        setCompanyDocumentsParam: setCompanyDocuments,
        setIsLoadingParam: setIsLoading,
        setIsFileUploaded,
        setFilesToSaveParam: setFilesToSave,
        autoClassify,
      };

      const fileCreateResponse = await validateAndUploadFile(fileProps);
      const response = fileCreateResponse as FileCreateResponse | undefined;

      let notificationMessage = 'Document automatically classified.';

      switch (response?.classification?.secondary_classification) {
        case SecondaryFileClassification.BALANCE_SHEET:
        case SecondaryFileClassification.CASH_FLOW_STATEMENT:
        case SecondaryFileClassification.INCOME_STATEMENT:
          notificationMessage = `This document was classified as a financial document(${response.classification.secondary_classification}) ${
            response.classification.auto_processing_skipped
              ? 'automatic mapping was not triggered. You can manually process it using "Create Labels" button in the Financials drawer.'
              : 'automatic mapping was triggered.'
          }`;
          break;
        case SecondaryFileClassification.CAP_TABLE:
          notificationMessage = 'This document was classified as a Cap Table document.';
          break;
        default:
          break;
      }

      enqueueSnackbar(notificationMessage, { variant: 'info', autoHideDuration: 8000 });
    };

    execUpload();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isValidType) {
    return <ProgressBar droppedFiles={droppedFiles} file={file} progress={progress} />;
  }

  return (
    <Alert isAlertVisible icon={<ErrorIcon />}>
      {EXTENSION_ERROR_MESSAGE(file)} {validFileTypes.join(', ').toUpperCase()}.
    </Alert>
  );
};

export default UploadedFileAlert;
